import React, { useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCoverflow } from "swiper/modules";
import { motion } from "framer-motion";
import { Shirt, Star } from "lucide-react";
import "swiper/css";
import "swiper/css/effect-coverflow";

const ClothesSwiper = ({
    items = [],
    selectedId = null,
    showDetails = true,
    heightClass = "h-[280px] sm:h-[350px] lg:h-[420px]",
    containerClass = "",
}) => {
    const swiperRef = useRef(null);
    // Détection mobile/tactile pour alléger les effets sur petits écrans
    const isTouch = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

    // Faire défiler vers l'élément sélectionné quand selectedId change
    useEffect(() => {
        if (!selectedId || !items.length) return;
        
        const selectedIndex = items.findIndex(item => item.id === selectedId);
        
        if (selectedIndex === -1) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn("[SWIPER] ⚠️ ID non trouvé:", selectedId);
            }
            return;
        }
        
        // Utiliser setTimeout pour s'assurer que le DOM est prêt
        const timeoutId = setTimeout(() => {
            if (swiperRef.current && swiperRef.current.slideTo && !swiperRef.current.destroyed) {
                const currentSlide = swiperRef.current.activeIndex;
                if (currentSlide !== selectedIndex) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.log(`[SWIPER] 🔄 Défilement ${currentSlide} → ${selectedIndex}`);
                    }
                    try {
                        swiperRef.current.slideTo(selectedIndex, 800);
                    } catch (error) {
                        if (process.env.NODE_ENV !== 'production') {
                            console.error("[SWIPER] ❌ Erreur défilement:", error);
                        }
                    }
                }
            }
        }, 200);
        
        return () => clearTimeout(timeoutId);
    }, [selectedId, items]);
    
    return (
        <div className={`w-full ${containerClass}`}>
            <Swiper
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                }}
                onTouchStart={(swiper, event) => {
                    // Protection contre l'erreur onTouchEnd
                    if (!swiper || !swiper.touches) return;
                }}
                onTouchEnd={(swiper, event) => {
                    // Protection contre l'erreur onTouchEnd
                    if (!swiper || !swiper.touches) return;
                }}
                effect={isTouch ? undefined : "coverflow"}
                grabCursor={true}
                centeredSlides={true}
                loop={false}
                coverflowEffect={isTouch ? undefined : {
                    rotate: 0,
                    stretch: 0,
                    depth: 160,
                    modifier: 1.8,
                    slideShadows: false,
                }}
                breakpoints={{
                    320: {
                        slidesPerView: 1.1,
                        spaceBetween: 12,
                    },
                    640: {
                        slidesPerView: 2.1,
                        spaceBetween: 14,
                    },
                    1024: {
                        slidesPerView: 3.1,
                        spaceBetween: 16,
                    },
                }}
                speed={isTouch ? 320 : 400}
                resistance={true}
                resistanceRatio={0.5}
                roundLengths={true}
                preloadImages={false}
                watchSlidesProgress={true}
                observer={true}
                observeParents={true}
                modules={[EffectCoverflow]}
                className={`outfit-swiper ${heightClass} py-6 [&_.swiper-pagination]:hidden [&_.swiper-button-next]:hidden [&_.swiper-button-prev]:hidden`}
            >
                {(items.length ? items : [{ id: "empty", __empty: true }]).map((item, index) => {
                    if (item.__empty) {
                        return (
                            <SwiperSlide key="empty">
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.85 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.08 }}
                                    className="flex h-full items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 text-xs font-medium text-gray-400 text-center px-4"
                                >
                                    Aucun vêtement affiché pour le moment
                                </motion.div>
                            </SwiperSlide>
                        );
                    }
                    const isSelected = selectedId === item.id;
                    return (
                        <SwiperSlide key={item.id}>
                            <motion.div
                                initial={{ opacity: 0, scale: 0.85 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.08 }}
                                className={`relative bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 overflow-hidden ${
                                    isSelected ? "ring-2 ring-purple-500 shadow-xl" : ""
                                }`}
                            >
                                <div className="relative aspect-square overflow-hidden">
                                    {item.image ? (
                                        <motion.img
                                            src={item.image}
                                            alt={item.type}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                            decoding="async"
                                            whileHover={{ scale: 1.05 }}
                                            transition={{ duration: 0.3 }}
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                                            <Shirt className="w-12 h-12 text-gray-400" />
                                        </div>
                                    )}

                                    {item.favori && (
                                        <motion.div
                                            className="absolute top-2 right-2"
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            transition={{ delay: index * 0.08 + 0.2, type: "spring" }}
                                        >
                                            <div className="bg-yellow-400 p-1.5 rounded-full shadow-sm">
                                                <Star className="w-3 h-3 text-white fill-current" />
                                            </div>
                                        </motion.div>
                                    )}
                                </div>

                                {showDetails && (
                                    <div className="p-3">
                                        <h3 className="font-medium text-gray-900 text-sm truncate capitalize">
                                            {item.title?.trim() || item.type}
                                        </h3>
                                        <p className="text-gray-500 text-xs capitalize">
                                            {Array.isArray(item.colors) && item.colors.length > 0
                                                ? item.colors.join(', ')
                                                : item.color || "Couleur non spécifiée"}
                                        </p>
                                    </div>
                                )}
                            </motion.div>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    );
};

export default ClothesSwiper;
